/* import __COLOCATED_TEMPLATE__ from './sync-status-filter.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';

interface Args {
  doSearch: (syncStatuses?: string[] | null) => void;
  selectedSyncStatusValues?: string[];
  activeFilter?: string;
  removeFilter: (syncStatus?: string) => void;
}

interface SelectOption {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class SyncStatusFilter extends Component<Args> {
  @service declare intl: IntlService;

  get selectedValues(): string[] {
    return this.args.selectedSyncStatusValues || [];
  }

  get isOpen() {
    return this.args.activeFilter === 'syncStatus' && !this.args.selectedSyncStatusValues?.length;
  }

  get items(): SelectOption[] {
    return [
      {
        text: this.intl.t('knowledge-hub.filterable-list.filters.sync-status.syncing'),
        value: SyncStatus.SYNCING,
        isSelected: this.selectedValues.includes(SyncStatus.SYNCING),
      },
      {
        text: this.intl.t('knowledge-hub.filterable-list.filters.sync-status.live'),
        value: SyncStatus.LIVE,
        isSelected: this.selectedValues.includes(SyncStatus.LIVE),
      },
      {
        text: this.intl.t('knowledge-hub.filterable-list.filters.sync-status.failed'),
        value: SyncStatus.ERROR,
        isSelected: this.selectedValues.includes(SyncStatus.ERROR),
      },
      {
        text: this.intl.t('knowledge-hub.filterable-list.filters.sync-status.excluded'),
        value: SyncStatus.FILTERED,
        isSelected: this.selectedValues.includes(SyncStatus.FILTERED),
      },
    ];
  }

  get details() {
    if (!this.selectedValues.length) {
      return this.intl.t('knowledge-hub.filterable-list.filters.sync-status.status');
    }

    let selectedItems = this.items.filter((item) => this.selectedValues.includes(item.value));
    return selectedItems.map((item) => item.text).join(', ');
  }

  @action removeFilter() {
    this.args.doSearch(null);
    this.args.removeFilter();
  }

  @action onSelectionChange(selectedValues: string[]) {
    if (selectedValues.length === 0) {
      this.args.doSearch(null);
      return;
    }

    // LIVE state needs to include both LIVE and PENDING_VERIFICATION
    let values = selectedValues.flatMap((value) =>
      value === SyncStatus.LIVE ? [SyncStatus.LIVE, SyncStatus.PENDING_VERIFICATION] : [value],
    );

    this.args.doSearch(values);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Filters::SyncStatusFilter': typeof SyncStatusFilter;
    'knowledge-hub/filterable-list/filters/sync-status-filter': typeof SyncStatusFilter;
  }
}
